import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, FileText, Trash2, BookOpen, Sparkles, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import WordConfirmModal from "../components/WordConfirmModal";

export default function WordLists() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [review, setReview] = useState(null); // { words, suggestedName, source } | null
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/wordlists");
      setLists(data);
    } catch { toast.error("Could not load lists"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const lower = file.name.toLowerCase();
    const ok = lower.endsWith(".pdf") || /\.(png|jpe?g|webp|heic|heif)$/.test(lower);
    if (!ok) { toast.error("Please pick a PDF or an image"); return; }
    const form = new FormData();
    form.append("file", file);
    setExtracting(true);
    try {
      const { data } = await api.post("/extract-words", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReview({
        words: data.words || [],
        suggestedName: data.suggested_name || file.name.replace(/\.[^.]+$/, ""),
        source: data.source || (lower.endsWith(".pdf") ? "pdf" : "image"),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read the file");
    } finally { setExtracting(false); }
  };

  const handleConfirm = async ({ name, words }) => {
    setSaving(true);
    try {
      const { data } = await api.post("/wordlists", { name, words });
      toast.success(`Added "${data.name}" with ${data.word_count} words!`);
      toast.info("Images will be generated after admin approval.", { duration: 5000 });
      setLists((prev) => [data, ...prev]);
      setReview(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save list");
    } finally { setSaving(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this word list?")) return;
    try {
      await api.delete(`/wordlists/${id}`);
      setLists((prev) => prev.filter((l) => l.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-6" data-testid="wordlists-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#1E1B4B]">Word Lists</h1>
          <p className="text-slate-600 mt-1 font-semibold">Upload a PDF or photo to start learning</p>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/webp,image/heic,image/heif"
          className="hidden"
          onChange={onFile}
          data-testid="pdf-file-input"
        />
        <button
          onClick={() => fileRef.current?.click()}
          className="btn-clay"
          disabled={extracting}
          data-testid="upload-pdf-btn"
        >
          <Upload className="w-5 h-5" strokeWidth={3} />
          {extracting ? "Reading…" : "Upload PDF or Photo"}
        </button>
      </div>

      {loading ? (
        <div className="card-soft text-center py-12 text-slate-500">Loading…</div>
      ) : lists.length === 0 ? (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", bounce: 0.4 }}
          className="card-soft text-center py-16"
          data-testid="wordlists-empty"
        >
          <div className="w-20 h-20 rounded-3xl bg-[#FBBF24] mx-auto flex items-center justify-center shadow-[0_8px_0_#B45309]">
            <Sparkles className="w-10 h-10 text-[#1E1B4B]" strokeWidth={3} />
          </div>
          <h2 className="font-display text-2xl font-bold text-[#1E1B4B] mt-5">No word lists yet</h2>
          <p className="text-slate-600 mt-2 max-w-md mx-auto">
            Ask a grown-up to upload a spelling bee PDF or a photo. We'll read the words for you!
          </p>
          <button
            onClick={() => fileRef.current?.click()}
            className="btn-clay mt-6"
            data-testid="upload-pdf-empty-btn"
          >
            <Upload className="w-5 h-5" strokeWidth={3} /> Upload PDF or Photo
          </button>
        </motion.div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wordlists-grid">
          {lists.map((l) => (
            <motion.div
              key={l.id}
              initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              className={`card-soft relative ${l.is_personal ? "border-4 border-amber-200" : ""}`}
              data-testid={`wordlist-item-${l.id}`}
            >
              {!l.is_personal && (
                <button
                  onClick={() => onDelete(l.id)}
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-rose-50 hover:bg-rose-100 text-[#F43F5E] flex items-center justify-center transition-colors"
                  title="Delete"
                  data-testid={`delete-wordlist-${l.id}`}
                >
                  <Trash2 className="w-4 h-4" strokeWidth={3} />
                </button>
              )}
              <div className={`w-11 h-11 rounded-xl ${l.is_personal ? "bg-[#FBBF24] shadow-[0_4px_0_#B45309]" : "bg-[#4D96FF] shadow-[0_4px_0_#1D4ED8]"} flex items-center justify-center text-white`}>
                {l.is_personal ? <Sparkles className="w-6 h-6 text-[#1E1B4B]" strokeWidth={3} /> : <FileText className="w-6 h-6" strokeWidth={3} />}
              </div>
              <div className="mt-3 font-display text-xl font-bold text-[#1E1B4B] line-clamp-2 pr-10">{l.name}</div>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <span className="chip">{l.word_count} words</span>
                {l.is_personal && (
                  <span
                    className="chip bg-amber-100 text-amber-800 border-amber-200 inline-flex items-center gap-1"
                    data-testid={`wordlist-personal-badge-${l.id}`}
                  >
                    <Lock className="w-3 h-3" strokeWidth={3} /> Personal
                  </span>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to={`/wordlists/${l.id}`} className="px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-[#1E1B4B] font-bold text-sm inline-flex items-center gap-1.5">
                  <BookOpen className="w-4 h-4" strokeWidth={3} /> View
                </Link>
                {l.is_personal && (
                  <Link
                    to="/my-words"
                    className="px-4 py-2 rounded-full bg-[#FBBF24] text-[#1E1B4B] font-bold text-sm shadow-[0_3px_0_#B45309] active:translate-y-[2px] active:shadow-[0_1px_0_#B45309] inline-flex items-center gap-1.5"
                    data-testid={`manage-my-words-${l.id}`}
                  >
                    <Pencil className="w-4 h-4" strokeWidth={3} /> Manage
                  </Link>
                )}
                {l.word_count > 0 && (
                  <Link to={`/practice/${l.id}`} className="px-4 py-2 rounded-full bg-[#4D96FF] text-white font-bold text-sm shadow-[0_3px_0_#1D4ED8] active:translate-y-[2px] active:shadow-[0_1px_0_#1D4ED8]">
                    Practice
                  </Link>
                )}
                {l.word_count > 0 && (
                  <Link to={`/test/${l.id}`} className="px-4 py-2 rounded-full bg-[#FF6B6B] text-white font-bold text-sm shadow-[0_3px_0_#9F1239] active:translate-y-[2px] active:shadow-[0_1px_0_#9F1239]">
                    Test
                  </Link>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <WordConfirmModal
        open={!!review}
        detectedWords={review?.words || []}
        suggestedName={review?.suggestedName || ""}
        source={review?.source || "pdf"}
        submitting={saving}
        nameLabel="List name"
        onCancel={() => (saving ? null : setReview(null))}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
